import { isAxiosError } from 'axios'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'

import { Loading } from '@/components/Loading'
import { AuthCard, AuthLayout } from '@/layouts/AuthLayout'
import API from '@/lib/api'
import { useAuth } from '@/lib/auth'
import type { ErrorResponse } from '@/types/ErrorResponse'

const refFromErrorType = (type: string | undefined): string => {
  if (type === 'user-not-found') {
    return 'verify_not_found'
  }

  if (type === 'already-verified') {
    return 'verify_already_verified'
  }

  return 'verify_token_invalid'
}

export default function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const token = searchParams.get('token')
  const started = useRef(false)
  const { t } = useTranslation(undefined, {
    keyPrefix: 'verifyEmail',
  })

  useAuth({
    middleware: 'guest',
    tmp: 'VerifyEmail',
  })

  useEffect(() => {
    if (started.current) {
      return
    }

    if (!email || !token) {
      navigate('/auth/login?ref=verify_token_invalid')
      return
    }

    started.current = true

    API.get('/api/v2/auth/csrf-token')
      .then(() => API.patch('/api/v2/auth/verify', { email, token }))
      .then(() => {
        navigate(
          `/auth/login?email=${encodeURIComponent(email)}&ref=verified`,
        )
      })
      .catch((error: unknown) => {
        const type = isAxiosError<ErrorResponse>(error)
          ? error.response?.data?.type
          : undefined

        navigate(`/auth/login?ref=${refFromErrorType(type)}`)
      })
  }, [email, token, navigate])

  return (
    <AuthLayout>
      <title>{t('title')}</title>

      <AuthCard>
        <Loading text="Ověřujeme váš e-mail" spinnerSize={24} />
      </AuthCard>
    </AuthLayout>
  )
}
